/**
 * Photos taken with no signal, kept in the browser until there is one.
 *
 * The upload manager holds its queue in memory, which is right for a desktop
 * drop of a hundred files and wrong for a receipt photographed in a basement.
 * This store writes every captured photo to IndexedDB the moment it is taken,
 * hands it to the upload manager when the device is online, and removes it
 * only when the API has confirmed the document. Closing the app loses nothing;
 * opening it again sends whatever is waiting.
 *
 * Background Sync would send without the app being opened; iOS does not
 * implement it, and the field is iPhones, so the honest promise is "sent the
 * next time you open the app with signal", and the page says exactly that.
 */

const DB_NAME = 'pathara-capture';
const STORE = 'pending';
const VERSION = 1;

export interface PendingCapture {
  readonly id: string;
  readonly organizationId: string;
  readonly filename: string;
  readonly mimeType: string;
  readonly byteSize: number;
  readonly blob: Blob;
  readonly capturedAt: string;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('organizationId', 'organizationId', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB would not open.'));
  });
}

function run<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = work(tx.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed.'));
        tx.oncomplete = () => db.close();
      }),
  );
}

export const captureStore = {
  /** Writes the photo before anything else happens to it. */
  async put(capture: PendingCapture): Promise<void> {
    await run('readwrite', (store) => store.put(capture));
  },

  async remove(id: string): Promise<void> {
    await run('readwrite', (store) => store.delete(id));
  },

  /** Everything waiting for this business, oldest first. */
  async list(organizationId: string): Promise<PendingCapture[]> {
    const all = await run<PendingCapture[]>(
      'readonly',
      (store) =>
        store.index('organizationId').getAll(organizationId) as IDBRequest<PendingCapture[]>,
    );
    return [...all].sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));
  },

  async count(organizationId: string): Promise<number> {
    return run<number>('readonly', (store) => store.index('organizationId').count(organizationId));
  },
};

/** A stored photo back as a File, named as it was taken, so the uploader sees no difference. */
export function toFile(capture: PendingCapture): File {
  return new File([capture.blob], capture.filename, {
    type: capture.mimeType,
    lastModified: new Date(capture.capturedAt).getTime(),
  });
}

/** `receipt-2026-10-30-14-05-22.jpg`: readable in the Inbox, unique enough. */
export function captureFilename(takenAt: Date, mimeType: string): string {
  const stamp = takenAt.toISOString().slice(0, 19).replace('T', '-').replaceAll(':', '-');
  const extension = mimeType === 'image/png' ? 'png' : mimeType === 'image/heic' ? 'heic' : 'jpg';
  return `receipt-${stamp}.${extension}`;
}
